import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, Star, TrendingUp, Trophy, User } from 'lucide-react';

export const socialProofMessenger: Playbook = {
  meta: {
    id: 'social-proof-messenger',
    slug: 'social-proof-messenger',
    categoryId: 'amplify-marketing',
    name: 'The Social Proof Messenger',
    tagline: 'Build trust with real-time activity.',
    description: 'Show real-time customer activity, testimonials, and success stories to build trust and credibility with potential customers.',
    strategy: 'Show real-time customer activity, testimonials, and success stories to build trust and credibility.',
    benefits: [
      'Build instant credibility',
      'Reduce buying hesitation',
      'Leverage customer success',
      'Increase conversion rates'
    ],
    targetAudience: ['B2B SaaS', 'E-commerce', 'Service businesses'],
    estimatedSetupTime: '18 minutes',
    difficulty: 'beginner',
    tags: ['social-proof', 'testimonials', 'credibility', 'trust-building'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Social Proof Messenger Playbook - Build Trust with a Genie',
    metaDescription: 'Leverage social proof with Genie messaging. Share customer success stories and real-time activity to build trust and credibility.',
    keywords: ['social proof', 'customer testimonials', 'trust building', 'credibility marketing', 'success stories']
  },
  demoConfig: {
    triggerType: 'delayed',
    delay: 4000,
    message: "🌟 Fun fact: 12 other companies in your industry are using our platform right now! Want to hear how TechCorp increased efficiency by 40%?",
    position: 'bottom-right'
  },
  demoScript: [
    { speaker: 'agent', message: "🌟 237 companies joined us this month! Want to hear their success stories?" },
    { speaker: 'user', message: "Sure, show me one" },
    { speaker: 'agent', message: "TechCorp reduced support tickets by 67% in just 2 weeks using our platform!" },
    { speaker: 'user', message: "That's impressive" },
    { speaker: 'agent', message: "They started with our Pro plan. Want to see how you could get similar results?" }
  ],
  templateConfig: {
    firstMessage: "🌟 Join [NUMBER] companies already succeeding with us! Want to hear their stories?",
    systemPrompt: "You are a social proof Genie. Share relevant testimonials, case studies, and success metrics to build trust. Be specific and authentic.",
    tools: ['testimonial_database', 'activity_feed', 'case_studies']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Prospect evaluates solution',
      description: 'Potential customer is researching options and comparing different providers'
    },
    {
      type: 'genie',
      icon: TrendingUp,
      genieIcon: 'genie-lamp',
      label: 'Shares relevant activity',
      description: 'Genie presents real-time data about customer growth, recent signups, or industry adoption'
    },
    {
      type: 'action',
      icon: Trophy,
      genieIcon: 'share',
      label: 'Highlights success stories',
      description: 'Shares specific customer testimonials and case studies relevant to prospect\'s industry'
    },
    {
      type: 'action',
      icon: Star,
      genieIcon: 'database',
      label: 'Provides specific metrics',
      description: 'Gives concrete numbers and results achieved by similar customers'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'target',
      label: 'Connects to prospect\'s needs',
      description: 'Relates success stories to prospect\'s specific use case and goals'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'trending-up',
      label: 'Increased trust and confidence',
      description: 'Prospect feels more confident about solution based on proven success with others'
    }
  ],
  metrics: {
    conversionRate: '19%',
    responseTime: '<1 second',
    satisfactionScore: '4.4/5',
    costSavings: '$40k/month',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'GrowthMetrics Solutions',
      authorName: 'Rachel Thompson',
      authorTitle: 'Marketing Director',
      quote: 'Social proof messaging increased our trial-to-paid conversion by 28%. Prospects trust us more when they see real success stories.',
      metrics: '28% increase in trial conversions'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Social Proof Message Library',
      description: 'Collection of proven testimonials and success story formats',
      url: '/templates/social-proof-messages'
    },
    {
      type: 'guide',
      title: 'Customer Success Story Collection',
      description: 'How to gather and organize compelling customer testimonials',
      url: '/docs/success-story-collection'
    }
  ]
};